import { MetricCardWithTimeSeriesProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricCardWithTimeSeries';
import { MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricDetailView';
import { TimestampedMeasure } from '@tableau/metrics-shared-widgets-dom/lib/src/models';
import { TabStyles } from '@tableau/widgets';
import { METRIC_CARD_HEIGHT } from '../app/spaces/CardView/CardViewStyles';
import { IMetricDataColumn, IMetricDataTable } from '../ts/ServerApi/types/IMetricDataTable';
import { IMetricSummary, IMetricWithSummaryData } from '../ts/ServerApi/types/IMetricWithSummaryData';
import { IMetricWithParsedData, ParsedMetricData } from './models';

export const metricCardPropsFromSummary = (summary: ParsedMetricData): MetricCardWithTimeSeriesProps => {
  return {
    testId: 'metric-card',
    valueStyle: {fontSize: TabStyles.Typography.Sizing.XXLarge},
    timeSeriesHeight: METRIC_CARD_HEIGHT,
    timeSeries: summary.data.slice(-100),
    lastUpdatedTimestamp: summary.lastUpdatedTimestamp,
    initialValue: summary.initialValue,
  };
};

export const metricDetailPropsFromMetric = (metric: IMetricWithSummaryData): MetricDetailViewProps => {
  return {
    metricName: metric.metric.name,
    testId: 'metric-detail',
    timeSeries: convertDataTable(metric.summary.dataTable),
    lastUpdatedTimestamp: validDateOrUndefined(metric.summary.clientTimestamp),
    initialValue: +metric.summary.value,
  };
};

export const parseMetrics = (metrics: ReadonlyArray<IMetricWithSummaryData>): ReadonlyArray<IMetricWithParsedData> => {
  return metrics.map(m => {
    return {
      metric: m.metric,
      parsedData: {
        data: convertDataTable(m.summary.dataTable),
        lastUpdatedTimestamp: validDateOrUndefined(m.summary.clientTimestamp),
        initialValue: isNullOrUndefined(m.summary.value) ? undefined : +m.summary.value,
      }
    };
  });
};

const validDateOrUndefined = (dateString?: string): Date | undefined => {
  if (isNullOrUndefined(dateString)) {
    return undefined;
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date;
};

export const convertDataTable = (dataTable?: IMetricDataTable): ReadonlyArray<TimestampedMeasure> | undefined => {
  if (!dataTable) {
    return undefined;
  }
  let dates: Date[] | undefined;
  let values: number[] | undefined;
  // TODO TFS 897524: Right now, we have to go off of the column's
  // type field to determine what information is there, but we want
  // the server to help us distinguish between the timestamp column
  // and the measure column
  if (dataTable) {
    dataTable.columns.forEach(column => {
      if (column.type === 'DATES' && column.dateTimes) {
        // in Javascript, `new Date(undefined)` and `new Date(null)` create valid dates
        // at the Unix Epoch, whereas `new Date('')` creates an invalid date instance.
        dates = column.dateTimes.map(date => isNullOrUndefined(date) ? new Date('') : new Date(date));
      } else {
        values = extractDataColumn(column);
      }
    });
  } else {
    return [];
  }

  if (values && dates) {
    const data = values.map((measure: number, i: number) => {
      if (measure === null) {
        measure = 0;
      }
      const timestamp = dates[i];
      if (isNaN(timestamp.getTime())) {
        return undefined;
      }
      return { timestamp, measure };
    }).filter(row => row !== undefined);
    return data;
  } else {
    return undefined;
  }
};

const isNullOrUndefined = (date: any) => date === undefined || date === null;

const extractDataColumn = (column: IMetricDataColumn): number[] | undefined => {
  let columnData;
  switch (column.type) {
    case 'LONGS': {
      columnData = column.longs;
      return columnData;
    }
    case 'DOUBLES': {
      columnData = column.doubles;
      return columnData;
    }
    // TODO TFS 897478: For now, we only support longs and doubles in
    // metrics dataTables, but there we need to understand how to better
    // handle different data types in the future, if at all
    case 'BOOLS':
    case 'STRINGS':
    default: {
      return;
    }
  }
};
